'use client';

import { ReactNode } from 'react';
import { Timer } from '@repo/libs/Timer';
import { useToastController } from '../../../../shared/toast/useToastController/useToastController';
import {
  DEFAULT_TOAST_DURATIONS,
  ToastDurations,
} from '../../../../shared/toast/useToast/Toast';
import { ToastAppearance } from './ToastAppearance';
import { ToastAlert } from './ToastAlert/ToastAlert';
import { ToastViewport } from './ToastViewport/ToastViewport';
import {
  ShowToastParams,
  ToastContext,
} from '../../../../shared/toast/ToastContext';

/**
 * The app's toast and the `showToast` behind `useToast`. One message at a
 * time, replaced not stacked (`useToastController` decides); this file is how
 * it looks in the logged-in apps.
 *
 * `timer` is injected so a spec can pass `new FakeTimer()`.
 *
 * A tap outside does not dismiss it; only its timer, close button or action
 * button do.
 */
export const ToastProvider = ({
  children,
  durations = DEFAULT_TOAST_DURATIONS,
  appearance,
  timer,
}: {
  children: ReactNode;
  durations?: ToastDurations;
  appearance?: ToastAppearance;
  timer?: Timer;
}) => {
  const { toast, showToast, closeToast, runAction } =
    useToastController<ShowToastParams>({
      timer,
    });

  return (
    <ToastContext.Provider value={{ showToast, durations }}>
      {children}
      {toast !== undefined && (
        <ToastViewport
          vertical={appearance?.vertical}
          horizontal={appearance?.horizontal}
        >
          <ToastAlert
            color={toast.severity}
            variant={appearance?.variant}
            width={appearance?.width}
            className={appearance?.severityClassNames?.[toast.severity]}
            action={
              toast.action === undefined
                ? undefined
                : { label: toast.action.label, onClick: runAction }
            }
            onClose={closeToast}
          >
            {toast.message}
          </ToastAlert>
        </ToastViewport>
      )}
    </ToastContext.Provider>
  );
};
